import React, { useState, useEffect } from 'react';
import type { Product } from '../types';
import { PhoneIcon, TrashIcon, PencilIcon, DuplicateIcon, EyeIcon, EyeSlashIcon, ArrowPathIcon, MapPinIcon, GavelIcon, ClockIcon, BoltIcon, ShareIcon, ChatBubbleLeftEllipsisIcon } from './Icons';
import { useAppStore } from '../store/useAppStore';
import ImageWithFallback from './ImageWithFallback';
import { analytics } from '../services/analytics';

interface ProductCardProps {
  product: Product;
  onEdit: (product: Product) => void;
  onDuplicate: (product: Product) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
  onViewDetails: (id: string) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onEdit, onDuplicate, onDelete, onToggleStatus, onViewDetails }) => {
  const { seller, openChat } = useAppStore();
  const [timeLeft, setTimeLeft] = useState<string>('');
  
  useEffect(() => {
      if (!product.isAuction || !product.auctionEndTime) return;
      
      const timer = setInterval(() => {
          const now = Date.now();
          const diff = product.auctionEndTime! - now;
          if (diff <= 0) {
              setTimeLeft('ENDED');
              clearInterval(timer);
          } else {
              const h = Math.floor(diff / 3600000);
              const m = Math.floor((diff % 3600000) / 60000);
              const s = Math.floor((diff % 60000) / 1000);
              setTimeLeft(`${h}h ${m}m ${s}s`);
          }
      }, 1000);
      
      return () => clearInterval(timer);
  }, [product.isAuction, product.auctionEndTime]);

  const handleContact = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!product.isActive) return;

    if (!seller) {
      alert("Please log in to chat with the seller.");
      return;
    }

    if (seller.contact === product.seller.contact) {
      alert("This is your product listing.");
      return;
    }

    analytics.trackClick('contact_seller_card', product.id);
    const chatId = `chat_${product.id}_${seller.id}`;
    openChat(chatId, product);
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `${window.location.origin}/products/${product.id}`;
    const shareData = {
      title: product.name,
      text: product.description,
      url
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        analytics.trackEvent('product_shared', { method: 'web_share', productId: product.id });
      } catch (err) {}
    } else {
      try {
        await navigator.clipboard.writeText(url);
        alert('Link copied!');
        analytics.trackEvent('product_shared', { method: 'clipboard_copy', productId: product.id });
      } catch (err) {}
    }
  };

  const handleCardClick = () => {
    onViewDetails(product.id);
  };

  const isOwner = seller && seller.contact === product.seller.contact;
  
  return (
    <div 
        onClick={handleCardClick}
        data-testid="product-card"
        className="product-card bg-card-bg text-card-text rounded-xl shadow-card hover:shadow-card-hover overflow-hidden cursor-pointer transition-all duration-300 flex flex-col relative group border border-card-border"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-brand-brown-50">
        <ImageWithFallback 
            src={product.imageUrl} 
            alt={product.name} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
        />
        
        {product.isPendingSync && (
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-brand-yellow px-2 py-0.5 rounded-full shadow-md z-10">
            <ArrowPathIcon className="w-3 h-3 text-btn-accent-text animate-spin" />
            <span className="text-[10px] font-black text-btn-accent-text uppercase">Pending Sync</span>
          </div>
        )}

        <button 
          onClick={handleShare}
          className="absolute bottom-2 right-2 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md z-10 text-brand-brown hover:bg-white transition-colors md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100"
          title="Share"
        >
          <ShareIcon className="w-3 h-3" />
        </button>

        {product.isCached && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-brand-green px-2 py-0.5 rounded-full shadow-md z-10" title="Served from Redis Cache">
            <BoltIcon className="w-3 h-3 text-white" />
          </div>
        )}

        {product.isAuction && (
             <div className="absolute top-2 left-2 flex items-center gap-1 bg-brand-yellow px-2 py-0.5 rounded-full shadow-md z-10">
                <GavelIcon className="w-3 h-3 text-btn-accent-text" />
                <span className="text-[10px] font-black text-btn-accent-text uppercase">Auction</span>
             </div>
        )}

        {product.distanceKm !== undefined && (
          <div className="absolute top-12 left-2 flex items-center gap-1 bg-white/90 backdrop-blur-sm px-2 py-0.5 rounded-full shadow-md z-10">
            <MapPinIcon className="w-3 h-3 text-brand-green" />
            <span className="text-[10px] font-black text-brand-green uppercase">
                {product.distanceKm < 1 ? 'Nearby' : `${product.distanceKm.toFixed(1)} km`}
            </span>
          </div>
        )}

        {product.isAuction && timeLeft && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-brand-green px-3 py-0.5 rounded-full shadow-md z-10 border border-white/20">
                <ClockIcon className="w-3 h-3 text-white animate-pulse" />
                <span className="text-[10px] font-black text-white uppercase tracking-wider">{timeLeft}</span>
            </div>
        )}

        {!product.isActive && (
            <div className="absolute inset-0 bg-app-text/40 flex items-center justify-center">
                <span className="bg-white/90 backdrop-blur-sm text-brand-brown px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                    Inactive
                </span>
            </div>
        )}

        {isOwner && (
            <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 z-20">
                <button 
                    onClick={(e) => { e.stopPropagation(); onToggleStatus(product.id); }}
                    className={`bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md hover:text-white transition-colors ${product.isActive ? 'text-brand-green-hover hover:bg-brand-green-hover' : 'text-app-text-secondary hover:bg-app-text-secondary'}`}
                    title={product.isActive ? "Deactivate" : "Activate"}
                >
                    {product.isActive ? <EyeIcon className="w-3 h-3" /> : <EyeSlashIcon className="w-3 h-3" />}
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); onDuplicate(product); }}
                    className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md text-brand-brown hover:bg-brand-brown hover:text-white transition-colors"
                    title="Duplicate"
                >
                    <DuplicateIcon className="w-3 h-3" />
                </button>
                <button 
                    onClick={(e) => { e.stopPropagation(); onEdit(product); }}
                    className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md text-brand-green hover:bg-brand-green hover:text-white transition-colors"
                    title="Edit"
                >
                    <PencilIcon className="w-3 h-3" />
                </button>
                <button 
                    onClick={(e) => { 
                        e.stopPropagation();
                        if(window.confirm('Delete this product?')) {
                            onDelete(product.id);
                        }
                    }}
                    className="bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md text-error-text hover:bg-error-text hover:text-white transition-colors"
                    title="Delete"
                >
                    <TrashIcon className="w-3 h-3" />
                </button>
            </div>
        )}
        <div className="absolute bottom-2 left-2">
            <span className="bg-brand-green text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider">
                {product.category}
            </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-1">
            <h3 className="text-lg font-black text-card-title leading-tight group-hover:text-brand-green-hover transition-colors truncate max-w-[70%]">{product.name}</h3>
            <div className="text-right flex flex-col">
                <span className="text-lg font-black text-brand-brown">
                    ${product.price.toFixed(2)}
                </span>
                <span className="text-[10px] text-app-text-secondary font-bold uppercase -mt-1">
                    {product.isAuction ? 'Start Bid' : `/ ${product.unit}`}
                </span>
            </div>
        </div>
        
        <p className="text-app-text-secondary text-sm mb-4 line-clamp-2 flex-grow">{product.description}</p>
        
        <div className="border-t border-divider pt-3 mt-auto">
          <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-brand-brown-50 rounded-full flex items-center justify-center text-[10px] font-bold text-brand-brown">
                {product.seller.name.charAt(0)}
              </div>
              <p className="text-xs text-app-text-secondary truncate">
                By <span className="font-bold text-brand-brown">{product.seller.name}</span>
              </p>
          </div>
          <button 
            onClick={handleContact}
            disabled={!product.isActive && !isOwner}
            className={`w-full flex items-center justify-center gap-2 font-black text-sm py-2.5 px-4 rounded-lg transition-all transform active:scale-95 shadow-sm ${
                product.isActive 
                ? (product.isAuction
                    ? 'bg-btn-accent text-btn-accent-text hover:bg-btn-accent-hover'
                    : 'bg-btn-primary text-btn-primary-text hover:bg-btn-primary-hover')
                : 'bg-btn-disabled-bg text-btn-disabled-text cursor-not-allowed'
            }`}
          >
            {product.isAuction ? <GavelIcon className="w-4 h-4" /> : <ChatBubbleLeftEllipsisIcon className="w-4 h-4" />}
            {product.isActive ? (product.isAuction ? 'Join Auction' : 'Contact Seller') : 'Inactive'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
